import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../../../components/shared/Table";
import { Box, Flex, useDisclosure, Spinner, Text } from "@chakra-ui/react";
import ToastComponent from "../../../../components/partials/ToastComponent";
import { columns } from "../columns/EmployeeListColumns"; // Adjust the path according to your file structure
import { useTranslation } from "react-i18next";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
// import EditEmployee from "../sidepanels/EditEmployee";
import CreateEmployee from "../sidepanels/CreateEmployee";
import ActiveFilter from "./partials/ActiveFilter";
// import IsFilterActive from "../../../../components/partials/IsFilterActive";
import useRoleCheck from "../../../../hooks/useRoleCheck";
import useThemeColors from "../../../../hooks/useThemeColors";
import { primary, textSecondary } from "../../../../utils/colors";
import SearchInput from "../../../../components/input/SearchInput";
import EmployeeStatsNew from "./partials/EmployeeStatsNew";
import TableContainerWindowed from "../../../../components/shared/Table/TableContainerWindowed";
import useDebounce from "../../../../hooks/useDebounce";
import PageHeader from "../../../../components/layout/PageHeader";
import useIsMobile from "../../../../hooks/useIsMobile";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
// import {
//   USER_CREATE_EMPLOYEE_RESET,
//   USER_DELETE_RESET,
// } from "../../../../constants/userConstants";
import {
  EMPLOYEE_CREATE_RESET,
  EMPLOYEE_DELETE_RESET,
  EMPLOYEE_UPDATE_RESET,
  EMPLOYEE_UPDATE_STATUS_RESET,
} from "../../../../constants/employeeConstants";
import {
  listEmployeesAction,
  deleteEmployeeAction,
  updateEmployeeStatusAction,
} from "../../../../actions/employeeActions";
import EditEmployee from "../sidepanels/EditEmployee";
import UserNoAccess from "../../../../components/partials/UserNoAccess";
import EmployeeStatsNewMobile from "./partials/EmployeeStatsNewMobile";
import CardList from "../../../../components/mobile/CardList";
import EmployeeCardMobile from "./partials/EmployeeCardMobile";
import DrawerNew from "../../../../components/menu/DrawerNew";

const EmployeeList = () => {
  // GENERAL
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const translate = (key) => t(key);
  const { whiteMode, textPrimaryMode } = useThemeColors();

  // STATE
  const [employeeIsCoordinatorInPlans, setEmployeeIsCoordinatorInPlans] =
    useState(false);

  // ROLES
  const isDoctor = useRoleCheck("employee_doctor");
  const isAdmin = useRoleCheck("admin");

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 100];

  // FETCHING LOADER
  const [isFetching, setIsFetching] = useState(false);

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
    setPage(1); // Reset page to 1 after search
  };

  // SIDEPANELS

  const [createEmployeeSidePanel, setCreateEmployeeSidePanel] = useState(false);
  const [editEmployeeSidePanel, setEditEmployeeSidePanel] = useState(false);

  // STATE
  const [userId, setUserId] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // State to control delete confirmation modal
  const [employeeName, setEmployeeName] = useState("");

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  const employeeList = useSelector((state) => state.employeeList);
  const {
    loading,
    error,
    employees,
    pages,
    countsByStatus,
    globalTotalCount,
    countsByProfession,
    countsByGender,
    genderPercentages,
  } = employeeList;

  const employeeCreate = useSelector((state) => state.employeeCreate);
  const {
    success: successCreate,
    error: errorCreate,
    loading: loadingCreate,
  } = employeeCreate;

  const employeeDelete = useSelector((state) => state.employeeDelete);
  const {
    success: successDelete,
    error: errorDelete,
    loading: loadingDelete,
  } = employeeDelete;

  const employeeUpdate = useSelector((state) => state.employeeUpdate);
  const {
    success: successUpdate,
    error: errorUpdate,
    loading: loadingUpdate,
  } = employeeUpdate;

  const employeeUpdateStatus = useSelector(
    (state) => state.employeeUpdateStatus
  );
  const {
    success: successUpdateStatus,
    error: errorUpdateStatus,
    loading: loadingUpdateStatus,
  } = employeeUpdateStatus;

  // FILTERING
  const [resetFilter, setResetFilter] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const { isOpenFilter, onOpenFilter, onCloseFilter } = useDisclosure();

  const filterConfig = {
    employees: true,
  };

  const [filterParams, setFilterParams] = useState({
    status: [],
    startDate: "",
    endDate: "",
    profession: [],
  });

  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setPage(1);
    setIsFilterActive(true);
    setResetFilter(false); // Reset the reset state after applying the filter
    localStorage.setItem("filterSettings_employees", JSON.stringify(filters));

    dispatch(
      listEmployeesAction(
        slug,
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        filters.status,
        filters.startDate,
        filters.endDate,
        filters.profession
      )
    );
  };

  const handleResetFilter = () => {
    const resetState = {
      startDate: "",
      endDate: "",
      status: [],
      profession: [],
    };
    setFilterParams(resetState);
    localStorage.setItem(
      "filterSettings_employees",
      JSON.stringify(resetState)
    ); // Reset localStorage

    // console.log("Resetting filters", resetState);
    console.log(
      "Resetting filters",
      localStorage.getItem("filterSettings_employees")
    );
    setResetFilter(true);
    setIsFilterActive(false);
    dispatch(
      listEmployeesAction(
        slug,
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        resetState.status,
        resetState.startDate,
        resetState.endDate,
        resetState.profession
      )
    );
  };

  //SORTING
  const [sortField, setSortField] = useState("employeeID");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSortChange = useCallback(
    (column) => {
      // Toggle sort order if the same column is clicked, or default to ascending
      const newSortOrder =
        sortField === column && sortOrder === "asc" ? "desc" : "asc";

      // Update sort field and order
      setSortField(column);
      setSortOrder(newSortOrder);

      // setPage(1);
    },
    [sortField, sortOrder]
  );

  // FILTER INACTIVE
  // Function to filter employees based on role
  const filterEmployeesByRole = (employees) => {
    // Non-admins see only active employees
    return (
      // employees && employees.filter((employee) => employee.status === "active")
      employees
    );
  };

  // HANDLERS

  const deleteHandler = (id) => {
    dispatch(deleteEmployeeAction(slug, id));
  };

  // SIDEPANEL HANDLERS

  const openCreateEmployeeSidePanelModal = () => {
    setCreateEmployeeSidePanel(true);
    document.body.classList.add("no-scroll");
  };

  const closeCreateEmployeeSidePanelModal = () => {
    setCreateEmployeeSidePanel(false);
    document.body.classList.remove("no-scroll");
  };

  const openEditEmployeeSidePanelModal = () => {
    setEditEmployeeSidePanel(true);
    document.body.classList.add("no-scroll");
  };
  const closeEditEmployeeSidePanelModal = () => {
    setEditEmployeeSidePanel(false);
    document.body.classList.remove("no-scroll");
  };

  // USEEFFECT
  useEffect(() => {
    if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === "") {
      setIsFetching(true);
      dispatch(
        listEmployeesAction(
          slug,
          debouncedSearchTerm,
          page,
          pageSize,
          sortField,
          sortOrder,
          filterParams.status,
          filterParams.startDate,
          filterParams.endDate,
          filterParams.profession
        )
      ).then(() => setIsFetching(false));
    }
  }, [
    debouncedSearchTerm,
    page,
    pageSize,
    sortField,
    sortOrder,
    filterParams,
    successCreate,
    successDelete,
    slug,
    successUpdate,
    successUpdateStatus,
  ]);

  useEffect(() => {
    if (successCreate) {
      toastComponent.showToast(t("toasts.employeeCreate"), "success", 3000);

      dispatch({ type: EMPLOYEE_CREATE_RESET });

      closeCreateEmployeeSidePanelModal();
    } else if (errorCreate) {
      toastComponent.showToast(t("toasts.employeeCreateError"), "error", 3000);
    }

    // eslint-disable-next-line
  }, [successCreate, errorCreate]);

  useEffect(() => {
    if (successDelete) {
      toastComponent.showToast(t("toasts.employeeDelete"), "success", 3000);
      dispatch({ type: EMPLOYEE_DELETE_RESET });
    } else if (errorDelete) {
      toastComponent.showToast(t("toasts.employeeDeleteError"), "error", 3000);
    }
    // eslint-disable-next-line
  }, [successDelete, errorDelete]);

  useEffect(() => {
    if (successUpdate) {
      toastComponent.showToast(t("toasts.employeeUpdate"), "success", 3000);

      closeEditEmployeeSidePanelModal();
    } else if (errorUpdate) {
      toastComponent.showToast(t("toasts.employeeUpdateError"), "error", 3000);
    }
    dispatch({ type: EMPLOYEE_UPDATE_RESET });

    // eslint-disable-next-line
  }, [successUpdate, errorUpdate]);

  useEffect(() => {
    if (successUpdateStatus) {
      toastComponent.showToast(
        t("toasts.employeeUpdateStatus"),
        "success",
        3000
      );
      closeEditEmployeeSidePanelModal();
    } else if (errorUpdateStatus) {
      toastComponent.showToast(
        t("toasts.employeeUpdateStatusError"),
        "error",
        3000
      );
    }
    dispatch({ type: EMPLOYEE_UPDATE_STATUS_RESET });
    // eslint-disable-next-line
  }, [successUpdateStatus, errorUpdateStatus]);

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess = currentTenantAccess?.permissions?.employees?.on;
  const hasAccessToCreate = currentTenantAccess?.permissions?.employees?.create;
  const hasAccessToDelete = currentTenantAccess?.permissions?.employees?.delete;
  const hasAccessToUpdate = currentTenantAccess?.permissions?.employees?.update;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    if (perms.patients?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.employees")} />
      </>
    );

  // LOADING
  const isLoading = loading || isFetching;

  const loggedInUserId = userInfo.employeeUserId;

  // LOGS
  // console.log("loggedInUserId", loggedInUserId);
  // console.log("employees", employees);
  // console.log("userInfo", userInfo);
  // console.log("countsByProfession", countsByProfession);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>{t("pageTitle.employees")}</title>
        <meta name="description" content={t("pageDescription.employees")} />
      </Helmet>

      {/* SIDEPANELS  */}
      {!isMobile ? (
        <SidePanel
          open={createEmployeeSidePanel}
          close={closeCreateEmployeeSidePanelModal}
          title={t("employee.createEmployee")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            {hasAccessToCreate ? (
              <CreateEmployee
                defaultRole="employee"
                close={closeCreateEmployeeSidePanelModal}
              />
            ) : (
              <UserNoAccess pageName={t("noAccess.employeeCreate")} p="0" />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={createEmployeeSidePanel}
          onClose={closeCreateEmployeeSidePanelModal}
          title={t("employee.createEmployee")}
          size="full"
        >
          {hasAccessToCreate ? (
            <CreateEmployee
              defaultRole="employee"
              close={closeCreateEmployeeSidePanelModal}
            />
          ) : (
            <UserNoAccess pageName={t("noAccess.employeeCreate")} p="0" />
          )}
        </DrawerNew>
      )}

      {!isMobile ? (
        <SidePanel
          open={editEmployeeSidePanel}
          close={closeEditEmployeeSidePanelModal}
          title={t("employee.editEmployee")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            <EditEmployee
              hasEditAccess={hasAccessToUpdate}
              slug={slug}
              // employee={employee}
              employeeId={userId}
              close={closeEditEmployeeSidePanelModal}
            />
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={editEmployeeSidePanel}
          onClose={closeEditEmployeeSidePanelModal}
          title={t("employee.editEmployee")}
          size="full"
        >
          <EditEmployee
            hasEditAccess={hasAccessToUpdate}
            slug={slug}
            // employee={employee}
            employeeId={userId}
            close={closeEditEmployeeSidePanelModal}
          />
        </DrawerNew>
      )}

      {/* MODALS  */}

      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={hasAccessToDelete}
          noAccessPageName={t("noAccess.employeeDelete")}
          deleteTitle={t("employee.deleteEmployeeTitle")}
          deleteName={employeeName}
          // warningLabel={t("employee.deleteWarning")}
          warningLabel={
            employeeIsCoordinatorInPlans === true
              ? t("employee.deleteWarningEmployeeInPlans")
              : t("employee.deleteWarning")
          }
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            if (employeeIsCoordinatorInPlans === true) {
              dispatch(updateEmployeeStatusAction(slug, userId, "inactive"));
            } else {
              deleteHandler(userId);
            }
            setShowDeleteConfirmation(false);
          }}
        />
      )}

      {/* MAIN  */}
      <Box w="100%" minH="100%" pt="80px">
        <Flex
          direction="column"
          w="100%"
          p={["0rem 0rem 0rem", "0rem 0rem 0rem", "2rem 2rem 6rem"]}
        >
          <Flex
            direction="column"
            w="100%"
            h="100%"
            bg={whiteMode}
            borderRadius={["0rem", "0rem", "2rem"]}
            overflow="hidden"
          >
            <PageHeader
              label={t("employee.employees")}
              buttonHandler={openCreateEmployeeSidePanelModal}
              buttonLabel={t("employee.createEmployee")}
              filter={
                <></>
                // <PatientsFilterPopover
                //   onFilterApply={handleFilterApply}
                //   isFilterActive={isFilterActive}
                //   setIsFilterActive={setIsFilterActive}
                //   filterStatusOptions="patient"
                //   savedFiltersKey="filterSettings_patients"
                //   resetFilter={resetFilter}
                //   onResetComplete={() => setResetFilter(false)}
                // />
              }
            />

            {!isMobile ? (
              <EmployeeStatsNew
                loading={loading}
                countsByStatus={countsByStatus || {}}
                globalTotalCount={globalTotalCount || 0}
                countsByProfession={countsByProfession || []}
                countsByGender={countsByGender || {}}
                genderPercentages={genderPercentages || {}}
                isFilterActive={isFilterActive}
              />
            ) : (
              <EmployeeStatsNewMobile
                loading={loading}
                countsByStatus={countsByStatus || {}}
                globalTotalCount={globalTotalCount || 0}
                countsByProfession={countsByProfession || []}
                countsByGender={countsByGender || {}}
                genderPercentages={genderPercentages || {}}
                isFilterActive={isFilterActive}
              />
            )}

            {isFilterActive && (
              <ActiveFilter
                filterParams={filterParams}
                setIsFilterActive={setIsFilterActive}
                onFilterApply={handleFilterApply}
                onReset={handleResetFilter}
              />
            )}

            <SearchInput
              placeholder={t("employee.search")}
              onChange={onChangeSearchTitle}
              searchTitle={searchTerm}
              p={["0 1.5rem", "0 2rem"]}
            />

            {!isMobile ? (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      columns={columns(
                        dispatch,
                        slug,
                        navigate,
                        isMobile,
                        textPrimaryMode,
                        whiteMode,
                        translate,
                        handleSortChange,
                        isAdmin,
                        loggedInUserId,
                        setShowDeleteConfirmation,
                        setUserId,
                        setEmployeeName,
                        deleteHandler,
                        setEmployeeIsCoordinatorInPlans,
                        openEditEmployeeSidePanelModal
                      )}
                      data={filterEmployeesByRole(employees)}
                      loading={loading}
                      error={error}
                      onRowClick={(userId) => {
                        // setUserId(userId);
                        // openEditEmployeeSidePanelModal();
                        // setEditEmployeeSidePanel(true);
                        navigate(`/${slug}/employees/${userId}`);
                      }}
                      noItemsTitle={t("noItems.noEmployees")}
                      noItemsDescription={t("noItems.noEmployeesText")}
                      noItemsButtonLabel={t("noItems.noEmployeesButton")}
                      noItemsButtonHandler={openCreateEmployeeSidePanelModal}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={globalTotalCount && globalTotalCount}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            ) : (
              <Flex
                w="100%"
                justify="center"
                align="flex-start"
                p="1rem 0rem"
                minH="800px"
              >
                <Box p="0" w="100%">
                  {employees && employees?.length > 0 ? (
                    <>
                      <CardList
                        slug={slug}
                        data={employees}
                        loading={loading}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSortChange={(field, order) => {
                          setSortField(field);
                          setSortOrder(order);
                          setPage(1); // restart pagination
                        }}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={globalTotalCount && globalTotalCount}
                        navigate={navigate}
                        title={t("employee.employees")}
                        noItemsText={t("noItems.noEmployees")}
                        renderItem={(employee) => (
                          <EmployeeCardMobile
                            key={employee._id}
                            data={employee}
                            onClick={() => {
                              setUserId(employee._id);
                              openEditEmployeeSidePanelModal();
                            }}
                            onDelete={() => {
                              setUserId(employee._id);
                              setEmployeeName(
                                `${employee.lastName} ${employee.firstName}`
                              );
                              setShowDeleteConfirmation(true);
                            }}
                          />
                        )}
                      />
                    </>
                  ) : (
                    <Text color={textSecondary}>
                      {t("noItems.noEmployees")}
                    </Text>
                  )}
                </Box>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Box>
    </>
  );
};

export default EmployeeList;
